// src/pages/purchaseOrder/PurchaseOrderA4Document.jsx
import React from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate } from '../../utils/dateUtils';
import A4DocumentLayout from '../../components/common/A4DocumentLayout/A4DocumentLayout';
import A4ProductCell from '../../components/common/A4DocumentLayout/A4ProductCell';

const PurchaseOrderA4Document = ({ purchaseOrder }) => {
  const { t } = useLocalization();

  if (!purchaseOrder) return null;

  const items = {
    columns: [
      { header: '#', width: '30px' },
      { header: t('purchaseOrder.itemProduct') },
      { header: t('purchaseOrder.orderedQuantity'), align: 'center' },
      { header: t('purchaseOrder.receivedQuantity'), align: 'center' },
      { header: t('purchaseOrder.itemUnitPrice'), align: 'right' },
      { header: t('purchaseOrder.itemLineTotal'), align: 'right' },
    ],
    rows: (purchaseOrder.items || []).map((item, idx) => {
      const sku = item.ProductVariation?.sku || item.Product?.sku;
      return [
        String(idx + 1),
        <A4ProductCell item={item} name={item.Product?.name || t('common.na')} sku={sku} />,
        parseFloat(item.quantity),
        parseFloat(item.receivedQuantity),
        formatCurrency(item.unitPrice),
        formatCurrency(item.lineTotal),
      ];
    }),
  };

  const totals = [
    { label: t('purchaseOrder.subtotal'), value: formatCurrency(purchaseOrder.totalAmount) },
  ];
  if (parseFloat(purchaseOrder.extraCharges) > 0) {
    totals.push({ label: t('purchaseOrder.extraCharges'), value: `+ ${formatCurrency(purchaseOrder.extraCharges)}` });
  }
  if (parseFloat(purchaseOrder.discountAmount) > 0) {
    totals.push({ label: t('purchaseOrder.discountAmount'), value: `- ${formatCurrency(purchaseOrder.discountAmount)}` });
  }
  totals.push({ label: t('purchaseOrder.paidAmount', 'Paid'), value: formatCurrency(purchaseOrder.paidAmount) });
  totals.push({ label: t('purchaseOrder.balanceAmount', 'Balance Due'), value: formatCurrency(purchaseOrder.balanceAmount) });
  totals.push({ label: t('purchaseOrder.grandTotal'), value: formatCurrency(purchaseOrder.grandTotal), variant: 'grand' });

  return (
    <A4DocumentLayout
      title={t('purchaseOrder.purchaseOrderDetails')}
      documentNumber={`#${purchaseOrder.purchaseOrderNumber}`}
      metaRows={[
        { label: t('purchaseOrder.orderDate'), value: formatDate(purchaseOrder.orderDate) },
        { label: t('common.status'), value: (purchaseOrder.status || '').toUpperCase() },
      ]}
      infoCards={[
        {
          title: t('purchaseOrder.supplierInformation'),
          primary: purchaseOrder.Supplier?.name || t('common.na'),
          lines: [`${t('purchaseOrder.supplierPhone')}: ${purchaseOrder.Supplier?.phone || t('common.na')}`],
        },
        {
          title: t('purchaseOrder.warehouseInformation'),
          primary: purchaseOrder.Warehouse?.name || t('common.na'),
          lines: [`${t('purchaseOrder.status')}: ${(purchaseOrder.paymentStatus || 'unpaid').toUpperCase()}`],
        },
      ]}
      items={items}
      summary={{
        notes: purchaseOrder.notes ? { label: t('purchaseOrder.notes'), text: purchaseOrder.notes } : undefined,
        totals,
      }}
      footerNote="This is a computer-generated purchase order."
    />
  );
};

export default PurchaseOrderA4Document;
